import React from 'react';
import { isString } from 'lodash';
import { Button } from 'components/Form';
import { Checkbox } from 'components/Form/Checkbox';
import {
  compose,
  withProps,
  withPropsOnChange,
  withStateHandlers,
  withState,
  withHandlers,
} from 'recompose';
import Dropzone from 'react-dropzone';
import imageToBase64 from './imageToBase64';
import { debounce } from 'lodash';
import cx from 'classnames';
import ReactCrop, { makeAspectCrop } from 'react-image-crop';
import FileUploader from 'components/Form/FileUploader';

import withField from '../withField';
import createLabel from '../createLabel';
import imageCropper from './cropImage';

import './crop.global.css';
import { Rhythm } from 'components/Layout/Rhythm';

import styles from './styles.css';
import Icon from 'components/Icon';
import { useTranslation } from 'react-i18next';

const preventDefault = (e) => e.preventDefault();
const cropImage = async (onChange, ...rest) => {
  const croppedImage = await imageCropper(...rest);
  return onChange(croppedImage);
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onabort = reject;
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const setInitialCrop = (image, aspect, setCrop) => {
  return setCrop(
    void 0,
    makeAspectCrop(
      {
        x: 0,
        y: 0,
        aspect,
        width: 100,
        height: 100,
        unit: '%',
      },
      image.width,
      image.height
    )
  );
};

export const ImageUploader = compose(
  withStateHandlers(
    ({ value, defaultValue, meta }) => ({
      src: value || defaultValue || (meta && meta.initial),
      crop: { width: 100, height: 100 },
      preview: void 0,
      allowAspect: false,
      isLoading: false,
    }),
    {
      setIsLoading: (state) => (isLoading) => ({ ...state, isLoading }),
      setPreview: (state) => (preview) => ({ ...state, preview }),
      setCrop: (state) => (_, crop) => ({ ...state, crop }),
      setSrc: (state) => (src) => ({ ...state, src }),
      toggleAspect: (state) => (e) => ({
        ...state,
        allowAspect: !state.allowAspect,
      }),
    }
  ),
  withPropsOnChange(['src'], ({ src }) => ({
    disableCrop: !src || src.startsWith('http') || src.startsWith('//'),
  })),

  withPropsOnChange(['label'], ({ label, name }) => {
    if (!label) return {};
    return {
      label: React.createElement(isString(label) ? createLabel(label) : label, {
        className: styles.label,
        htmlFor: name,
      }),
    };
  }),

  withHandlers({
    onUpload:
      ({ setSrc, onChange, setIsLoading, aspect, setCrop }) =>
      async (files) => {
        setIsLoading(true);
        const file = await loadImage(files[0]);
        const img = await imageToBase64(file);
        setIsLoading(false);
        setSrc(img);
        setInitialCrop(img, aspect, setCrop);
        return onChange(img);
      },
    onDelete:
      ({ setSrc, onChange }) =>
      () => {
        setSrc();
        onChange();
      },
    onImageLoaded:
      ({ aspect, setCrop }) =>
      (image) =>
        setInitialCrop(image, aspect, setCrop),
    onComplete:
      ({ src, onChange, maxWidth = 1480 }) =>
      (_, crop) =>
        cropImage(onChange, src, crop, maxWidth),
  }),

  withPropsOnChange(
    ['value, defaultValue'],
    ({ value, defaultValue, onDelete }) => !value && !defaultValue && onDelete()
  )
)(
  ({
    onUpload,
    onCrop,
    onDelete,
    label,
    aspect,
    crop,
    setCrop,
    onComplete,
    placeholder,
    disableCrop,
    src,
    className,
    allowAspect,
    toggleAspect,
    allowChangeAspect,
    onImageLoaded,
    isLoading,
    error,
    preview,
    allowRemove,
    description,
  }: any) => {
    const { t } = useTranslation('search');
    return (
      <div className={cx(styles.wrapper, className)}>
        <div className={styles.root}>
          {label}
          <Rhythm vertical>
            {src && (
              <ReactCrop
                src={src}
                keepSelection
                ruleOfThirds
                onChange={setCrop}
                onImageLoaded={onImageLoaded}
                onComplete={onComplete}
                disabled={disableCrop}
                crop={{
                  ...crop,
                  unit: '%',
                  aspect: allowAspect ? void 0 : aspect,
                }}
              />
            )}
            {allowChangeAspect && !!src && !disableCrop && (
              <Checkbox
                label="Set custom aspect ratio"
                defaultValue={allowAspect}
                onChange={toggleAspect}
              />
            )}
            <Rhythm>
              <FileUploader
                accept="image/jpeg, image/png, image/gif"
                multiple={false}
                onChange={onUpload}
                isLoading={isLoading}
                label={t('banners.create_banner.upload_image_button')}
              />
              <Button
                display-if={allowRemove && src}
                type="warning"
                onClick={onDelete}
              >
                Remove
              </Button>
            </Rhythm>
          </Rhythm>
        </div>
        {description && <div className={styles.description}>{description}</div>}
        {error && <div className={styles.error}>{error}</div>}
      </div>
    );
  }
);

export default withField(ImageUploader);
